<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDataLoadStore } from '../../stores/dataLoadStore';

const store = useDataLoadStore();
const { t } = useI18n();

// Two-step confirmation for the destructive swap. Reset whenever the dialog
// closes or the chosen backup changes so a stale "are you sure?" never lingers.
const confirmingSwap = ref(false);

watch(
    () => [store.isOpen, store.selectedBackup],
    () => {
        confirmingSwap.value = false;
    },
);

function requestSwap() {
    confirmingSwap.value = true;
}

async function confirmSwap() {
    confirmingSwap.value = false;
    await store.swapCaseData();
}
</script>

<template>
    <div v-if="store.isOpen" class="dl-overlay" @click.self="store.close()">
        <div class="dl-modal">
            <div class="dl-modal-close-strip">
                <button class="dl-modal-close" type="button" @click="store.close()">
                    <span class="material-icons" style="font-size: 18px">close</span>
                    <span>{{ t('common.close') }}</span>
                </button>
            </div>

            <div class="dl-modal-header">
                <span class="material-icons dl-modal-header-icon">storage</span>
                <span class="dl-modal-title">{{ t('dataLoad.title') }}</span>
            </div>

            <div class="dl-modal-body">
                <p class="dl-description">
                    {{ t('dataLoad.description') }}
                </p>

                <!-- Result summary -->
                <div v-if="store.scanStatus === 'complete' && store.scanSummary" class="dl-result">
                    <span class="material-icons dl-result-icon dl-result-icon-ok">check_circle</span>
                    <div class="dl-result-text">
                        <div class="dl-result-headline">{{ t('dataLoad.scanComplete') }}</div>
                        <div class="dl-result-detail">
                            {{ t('dataLoad.scanSummary', {
                                columnsWithData: store.scanSummary.columnsWithData,
                                totalColumns: store.scanSummary.totalColumns,
                                totalTables: store.scanSummary.totalTables,
                            }) }}
                        </div>
                    </div>
                </div>

                <!-- Error -->
                <div v-if="store.scanStatus === 'error'" class="dl-result">
                    <span class="material-icons dl-result-icon dl-result-icon-err">error_outline</span>
                    <div class="dl-result-text">
                        <div class="dl-result-headline">{{ t('dataLoad.scanFailed') }}</div>
                        <div class="dl-result-detail">{{ store.errorMessage }}</div>
                    </div>
                </div>

                <!-- Swap case data: replace the cdm-namespace data from a
                     server backup. Application data (sessions, chats, etc.) is
                     never touched. -->
                <div class="dl-swap">
                    <div class="dl-swap-divider"></div>
                    <div class="dl-swap-title">{{ t('dataLoad.swap.title') }}</div>
                    <p class="dl-description">
                        {{ t('dataLoad.swap.description') }}
                    </p>

                    <div class="dl-swap-controls">
                        <select
                            v-model="store.selectedBackup"
                            class="dl-swap-select"
                            :disabled="store.isSwapping || store.availableBackups.length === 0"
                        >
                            <option :value="null" disabled>
                                {{
                                    store.availableBackups.length === 0
                                        ? t('dataLoad.swap.noBackups')
                                        : t('dataLoad.swap.selectBackup')
                                }}
                            </option>
                            <option
                                v-for="name in store.availableBackups"
                                :key="name"
                                :value="name"
                            >
                                {{ name }}
                            </option>
                        </select>

                        <button
                            v-if="!confirmingSwap"
                            class="dl-footer-btn dl-footer-btn-danger"
                            type="button"
                            :disabled="!store.selectedBackup || store.isSwapping"
                            @click="requestSwap"
                        >
                            <span v-if="store.isSwapping" class="dl-scan-btn-spinner"></span>
                            <span v-else class="material-icons" style="font-size: 18px">swap_horiz</span>
                            {{ store.isSwapping ? t('dataLoad.swap.replacing') : t('dataLoad.swap.replace') }}
                        </button>
                    </div>

                    <!-- Inline confirmation step. -->
                    <div v-if="confirmingSwap" class="dl-swap-confirm">
                        <span class="dl-swap-confirm-text">
                            {{ t('dataLoad.swap.confirmText') }}
                        </span>
                        <div class="dl-swap-confirm-actions">
                            <button
                                class="dl-footer-btn dl-footer-btn-secondary"
                                type="button"
                                @click="confirmingSwap = false"
                            >
                                {{ t('common.cancel') }}
                            </button>
                            <button
                                class="dl-footer-btn dl-footer-btn-danger"
                                type="button"
                                @click="confirmSwap"
                            >
                                {{ t('dataLoad.swap.confirmYes') }}
                            </button>
                        </div>
                    </div>

                    <!-- Swap result / error. -->
                    <div v-if="store.swapStatus === 'complete'" class="dl-result">
                        <span class="material-icons dl-result-icon dl-result-icon-ok">check_circle</span>
                        <div class="dl-result-text">
                            <div class="dl-result-headline">{{ t('dataLoad.swap.completeHeadline') }}</div>
                            <div class="dl-result-detail">
                                {{ t('dataLoad.swap.completeDetail') }}
                            </div>
                        </div>
                    </div>
                    <div v-if="store.swapStatus === 'error'" class="dl-result">
                        <span class="material-icons dl-result-icon dl-result-icon-err">error_outline</span>
                        <div class="dl-result-text">
                            <div class="dl-result-headline">{{ t('dataLoad.swap.failedHeadline') }}</div>
                            <div class="dl-result-detail">{{ store.swapErrorMessage }}</div>
                        </div>
                    </div>
                </div>

                <!-- One-off code migration: back-fill Matter Data Subsets for
                     query results stored in the legacy inline format. Safe to
                     re-run; already-migrated results are skipped. -->
                <div class="dl-swap">
                    <div class="dl-swap-divider"></div>
                    <div class="dl-swap-title">Migrate legacy query results</div>
                    <p class="dl-description">
                        Older query results are stored in a legacy format and
                        won't show references until migrated. Runs once per
                        matter; re-running skips anything already migrated.
                    </p>

                    <div class="dl-swap-controls">
                        <button
                            class="dl-footer-btn dl-footer-btn-secondary"
                            type="button"
                            :disabled="store.isMigrating"
                            @click="store.runLegacyResultsMigration()"
                        >
                            <span v-if="store.isMigrating" class="dl-scan-btn-spinner"></span>
                            <span v-else class="material-icons" style="font-size: 18px">published_with_changes</span>
                            {{ store.isMigrating ? 'Migrating…' : 'Migrate' }}
                        </button>
                    </div>

                    <div v-if="store.migrationStatus === 'complete' && store.migrationSummary" class="dl-result">
                        <span class="material-icons dl-result-icon dl-result-icon-ok">check_circle</span>
                        <div class="dl-result-text">
                            <div class="dl-result-headline">Migration complete</div>
                            <div class="dl-result-detail">
                                {{ store.migrationSummary.migrated }} of
                                {{ store.migrationSummary.total_legacy }} legacy
                                queries migrated
                                ({{ store.migrationSummary.skipped_zero_result }} had
                                no results<template v-if="store.migrationSummary.failed">,
                                {{ store.migrationSummary.failed }} failed</template>).
                            </div>
                        </div>
                    </div>
                    <div v-if="store.migrationStatus === 'error'" class="dl-result">
                        <span class="material-icons dl-result-icon dl-result-icon-err">error_outline</span>
                        <div class="dl-result-text">
                            <div class="dl-result-headline">Migration failed</div>
                            <div class="dl-result-detail">{{ store.migrationErrorMessage }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer holds the primary actions, consistent with other dialogs. -->
            <div class="dl-modal-footer">
                <button
                    class="dl-footer-btn"
                    :class="
                        store.scanStatus === 'complete'
                            ? 'dl-footer-btn-secondary'
                            : 'dl-footer-btn-primary'
                    "
                    type="button"
                    :disabled="store.isScanning"
                    @click="store.runScan()"
                >
                    <span v-if="store.isScanning" class="dl-scan-btn-spinner"></span>
                    <span v-else class="material-icons" style="font-size: 18px">manage_search</span>
                    {{
                        store.isScanning
                            ? t('dataLoad.scanning')
                            : store.scanStatus === 'complete'
                              ? t('dataLoad.scanAgain')
                              : t('dataLoad.scanNow')
                    }}
                </button>
                <button
                    v-if="store.scanStatus === 'complete'"
                    class="dl-footer-btn dl-footer-btn-primary"
                    type="button"
                    @click="store.close()"
                >
                    {{ t('common.close') }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.dl-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: var(--z-modal-elevated);
    display: flex;
    align-items: center;
    justify-content: center;
}

.dl-modal {
    width: 480px;
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.dl-modal-close-strip {
    padding: 6px 14px;
    border-bottom: 1px solid var(--color-border);
}

.dl-modal-close {
    background: transparent;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--text-base);
    color: var(--color-text-primary);
    cursor: pointer;
    padding: 4px 8px;
}

.dl-modal-close:hover {
    color: var(--color-primary);
}

.dl-modal-header {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid var(--color-border);
    background: var(--color-header-bg);
}

.dl-modal-header-icon {
    font-size: 20px;
    color: var(--color-text-secondary);
}

.dl-modal-title {
    font-size: var(--text-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.dl-modal-body {
    padding: 20px 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.dl-description {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    line-height: 1.5;
    margin: 0;
}

.dl-scan-btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: dl-spin 0.7s linear infinite;
    flex-shrink: 0;
}

@keyframes dl-spin {
    to { transform: rotate(360deg); }
}

.dl-result {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: var(--color-bg-subtle, #f8f9fa);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
}

.dl-result-icon {
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 1px;
}

.dl-result-icon-ok {
    color: var(--color-success, #2e7d32);
}

.dl-result-icon-err {
    color: var(--color-error, #c62828);
}

.dl-result-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.dl-result-headline {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.dl-result-detail {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    line-height: 1.4;
}

.dl-modal-footer {
    padding: 12px 20px 16px;
    border-top: 1px solid var(--color-border);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.dl-footer-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: opacity 0.15s, background 0.15s;
}

.dl-footer-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.dl-footer-btn-primary {
    background: var(--color-primary);
    color: #fff;
}

.dl-footer-btn-primary:not(:disabled):hover {
    opacity: 0.88;
}

.dl-footer-btn-secondary {
    background: var(--color-bg-white);
    color: var(--color-text-primary);
    border-color: var(--color-border);
}

.dl-footer-btn-secondary:not(:disabled):hover {
    background: var(--color-bg-light);
}

.dl-footer-btn-danger {
    background: var(--color-error, #c62828);
    color: #fff;
}

.dl-footer-btn-danger:not(:disabled):hover {
    opacity: 0.88;
}

.dl-swap {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dl-swap-divider {
    border-top: 1px solid var(--color-border);
    margin: 4px 0;
}

.dl-swap-title {
    font-size: var(--text-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.dl-swap-controls {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dl-swap-select {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    background: var(--color-bg-white);
}

.dl-swap-select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.dl-swap-confirm {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    background: var(--color-bg-subtle, #f8f9fa);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
}

.dl-swap-confirm-text {
    font-size: var(--text-sm);
    color: var(--color-text-primary);
}

.dl-swap-confirm-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}
</style>
